/**
 * "The AI is working" — true while any chat tab has a turn running, for surfaces outside the
 * panel (the header's AI button spins its ring on it).
 *
 * The panel's per-tab `isLoading` is component state, so it is published here instead of being
 * read off the instance: a reader then re-renders when it changes, not only when something else
 * happens to re-render it. A turn that stops to ask the user (an approve/reject gate) clears
 * `isLoading`, so it reads as not working — the AI is waiting for the user, not busy.
 *
 * The panel stays mounted while the chat is hidden, so the signal keeps flowing with the chat
 * closed; it drops to false when the panel unmounts (leaving the designer, the report designer).
 */
export const MCP_CHAT_BUSY_EVENT: string = "mcp-chat-busy";

interface IMcpChatBusyDetail {
  busy: boolean;
}

let isBusy: boolean = false;

/** Called by the panel on every update; only a change is announced. */
export const publishMcpChatBusy = (busy: boolean): void => {
  if (busy === isBusy) return;
  isBusy = busy;
  window.dispatchEvent(new CustomEvent<IMcpChatBusyDetail>(MCP_CHAT_BUSY_EVENT, { detail: { busy } }));
};

export const isMcpChatBusy = (): boolean => isBusy;

/** Returns the unsubscribe. The listener gets each change, not the current value — read that first. */
export const subscribeMcpChatBusy = (listener: (busy: boolean) => void): (() => void) => {
  const handler: (event: Event) => void = (event: Event): void => {
    listener((event as CustomEvent<IMcpChatBusyDetail>).detail.busy);
  };
  window.addEventListener(MCP_CHAT_BUSY_EVENT, handler);
  return (): void => window.removeEventListener(MCP_CHAT_BUSY_EVENT, handler);
};
